import { useEffect, useContext, useState, useRef } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { useNavigate, useParams } from "react-router";
import { StoreContext } from "../../store/Store";
import { APIContext } from "../../store/APIStore";
import IReservation from "../../dataProvider/interfaces/ISessionReservation";
import { Loading, useRedirect } from "react-admin";
import { useForm, Controller } from "react-hook-form";
import  {
  createFilterOptions,
} from '@mui/material/Autocomplete';
import CloseIcon from "@mui/icons-material/Close";
import { routePaths } from "../../baseLayout/SidebarMenu";
import IAttendee from "../../dataProvider/interfaces/IAttendee";
import debounce from "lodash.debounce";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import {
  actionDialogType,
  RouteLeavingGuard,
  SimpleActionDialog,
} from "../../components";
import { BiTrash } from "react-icons/bi";
import { useHasPermissions } from "../../hooks/useHasPermissions";
import { PERMISSIONS } from "../../roles";
// import { formHasChangedValues } from "../../components/RouteLeavingGuard/RouteLeavingGuard";
import ISessionReservation from "../../dataProvider/interfaces/ISessionReservation";
import {reservationEdit as classes} from "./styles";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import FdButton from "../../components/UIComponents/Button/FdButton";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
import FdAutoComplete from "../../components/UIComponents/AutoComplete/FdAutoComplete";

const ReservationEdit = () => {
  const redirect = useRedirect();
  const { eventId, sessionId, reservationId } = useParams() as any;
  const [state, dispatch] = useContext(StoreContext);
  const api = useContext(APIContext);
  const [
    reservationDetails,
    setReservationDetails,
  ] = useState<IReservation | null>(null);
  const [loading, setLoading] = useState(true);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [eventAttendees, setEventAttendees] = useState<IAttendee[]>([]);
  const [selectedAttendee, setSelectedAttendee] = useState<IAttendee>();
  const [confirmationDialog, setConfirmationDialog] = useState<any>({
    open: false,
    title: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
    onConfirm: () => {},
  });
  const [dirtyForm, setDirtyForm] = useState(false);
  const filter = createFilterOptions();
  const notificationRef = useRef<any>();
  let navigate = useNavigate();
  // let submit: Function;
  // let reservationForm: any;

  // permissions
  const canManageReservation = useHasPermissions(
    PERMISSIONS.CAN_MANAGE_RESERVATION
  );

  useEffect(() => {
    // load selected event
    if (eventId && !state.selectedEvent) {
      fetchSelectedEvent();
    }

    if (reservationId) {
      loadReservationDetails();
    } else {
      setLoading(false);
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // set reservation attendee
    if (reservationDetails) {
      setSelectedAttendee({
        id: reservationDetails.attendeeId,
        name: reservationDetails.attendeeName,
      } as IAttendee);
    }
  }, [reservationDetails]);

  const searchAttendees = async (searchKey: string) => {
    setAttendeesLoading(true);
    const { content } = (await api.attendeeService.searchAttendees(
      state.selectedEvent?.id,
      {
        search: searchKey,
        page: 0,
        limit: 50,
      }
    )) as any;

    setAttendeesLoading(false);
    setEventAttendees(content);
  };

  const fetchSelectedEvent = async () => {
    try {
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const fetchSessionReservations = async (): Promise<ISessionReservation[]> => {
    try {
      const reservations = await api.sessionService.getSessionReservations(
        sessionId,
        eventId
      );
      return reservations;
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
      return [];
    }
  };

  const loadReservationDetails = async () => {
    if (state.sessionReservations) {
      const reservation = state.sessionReservations.filter(
        (reservation: IReservation) =>
          reservation.id === parseInt(reservationId, 10)
      )[0];
      setReservationDetails(reservation);
      setLoading(false);
    } else {
      const reservations = await fetchSessionReservations();
      dispatch({ type: "SET_SESSION_RESERVATIONS", payload: reservations });
      if (reservations) {
        const reservation = reservations.filter(
          (reservation: IReservation) =>
            reservation.id === parseInt(reservationId, 10)
        )[0];
        setReservationDetails(reservation);
      }
      setLoading(false);
    }
  };

  const onSubmit = async (values: any) => {
    // reservationForm.reset();
    setDirtyForm(false);
    if (reservationDetails) {
      updateReservation(values);
    } else {
      createReservation(values);
    }
  };

  const updateReservation = async (values: any) => {
    const data = {
      amount: values.amount,
      accessAmount: values.amount,
      message: values.message,
    };

    try {
      if (reservationDetails) {
        await api.attendeeService.updateReservation(
          eventId,
          reservationDetails.id,
          data
        );
        // reset added for updated field to avoid routerLeavingGuard and added attendee field to avoid undefined attendee name.
        reset({
          ...data,
          attendee: reservationDetails?.attendeeName,
        })
        notificationRef.current?.show(
          "Reservation successfully saved",
          notificationType.Success
        );
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const createReservation = async (values: any) => {
    const data = {
      amount: values.amount,
      accessAmount: values.amount,
      message: values.message,
    };

    try {
      if (selectedAttendee) {
        const result: any = await api.attendeeService.createReservation(
          selectedAttendee.id,
          eventId,
          sessionId,
          data
        );
        reset({
          amount: result?.amount,
          message: result?.message,
          attendee: result?.attendeeName
        })
        notificationRef.current?.show(
          "Reservation successfully saved",
          notificationType.Success
        );
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleDeleteReservation = async () => {
    try {
      if (reservationDetails) {
        await api.attendeeService.removeReservation(
          reservationDetails?.attendeeId,
          eventId,
          reservationDetails?.eventSessionId
        );
      }
      redirect(
        `/${routePaths.EVENTS}/${eventId}/${routePaths.SESSIONS}/${sessionId}/${routePaths.RESERVATIONS}`
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleAttendeeSearch = debounce((e: any) => {
    const typedValue = e.target.value;
    if (typedValue.length === 0) {
      setEventAttendees([]);
    } else {
      searchAttendees(typedValue);
    }
  }, 500);

  const showDeleteDialog = () => {
    setConfirmationDialog({
      open: true,
      title: "Are you sure you want to delete this reservation?",
      icon: <BiTrash size="45" />,
      type: actionDialogType.WARNING,
      isLoading: false,
      onConfirm: () => {
        dirtyForm && setDirtyForm(false); // we don't want to show the form leave warning when redirecting after deletion
        handleDeleteReservation();
      },
      onCancel: handleDialogClose,
    });
  };

  const handleDialogClose = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false,
    });
  };

  const initialValues = {
    amount: reservationDetails?.amount,
    message: reservationDetails?.message,
    attendee: reservationDetails?.attendeeName,
  };
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { isDirty, errors },
  } = useForm({
    defaultValues: initialValues,
    mode: "onBlur",
  });

  useEffect(() => {
    reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, reservationDetails])

  if (loading) {
    return <Loading />;
  }

  return <>
    <Box sx={classes.wrapper}>
      <Box sx={classes.contentWrapper}>
        <Box sx={classes.topActions}>
          <Box sx={classes.infoTitle}>
            <Box textAlign="left">
            <FdTypography
              label={reservationDetails?.attendeeName || "New reservation"}
                variant="h2"
                component="h2"
                mb="10px"
              />
              <Box display="flex" alignItems="center">
              <FdTypography label="Third Party ID:" />
              <FdButton
                sx={classes.attendeeThirdPartyId}
                onClick={() => {
                  redirect(
                    `/${routePaths.EVENTS}/${eventId}/${routePaths.ATTENDEES}/${reservationDetails?.attendeeId}/${routePaths.EDIT}`
                  );
                }}
                ButtonName={reservationDetails?.attendeeThirdPartyId}
                variant="text"
              />
              </Box>
            </Box>
          </Box>

          {reservationDetails && canManageReservation && (
            <>
             <FdButton
                sx={{ marginLeft: "15px" }}
                startIcon={<BiTrash />}
                onClick={() => {
                  showDeleteDialog();
                }}
                ButtonName="Delete"
                variant="text"
              />
            </>
          )}

          {canManageReservation && (
            <FdButton
            size="medium"
            sx={classes.saveButton}
            ButtonName="Save"
            form="reservationForm"
            type="submit"
          />
          )}
          <IconButton
            aria-label="close"
            onClick={() => {
              redirect(
                `/${routePaths.EVENTS}/${eventId}/${routePaths.SESSIONS}/${sessionId}/${routePaths.RESERVATIONS}`
              );
            }}
            size="large">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={classes.formContent}>
                <form id="reservationForm" onSubmit={handleSubmit(onSubmit)} noValidate
                  style={{
                    width: "100%",
                    textAlign: "left",
                    height: "100%",
                    display: "contents",
                  }}
                >
                  <Box sx={classes.genericFields}>
                    <Box sx={classes.formFields}>
                      {/* attendee input */}
                      <Box marginBottom="15px">
                      <Controller
                          control={control}
                          name="attendee"
                          rules={{
                            required: true,
                          }}
                          render={({ field: { onChange, value } }) => (
                            <>
                              <FdAutoComplete
                                //@ts-ignore
                                defaultValue={
                                  reservationDetails &&
                                  ({
                                    id: reservationDetails?.attendeeId,
                                    name: reservationDetails?.attendeeName,
                                  } as IAttendee)
                                }
                                onChange={(e: any, value: any) => {
                                  setDirtyForm(true);
                                  setSelectedAttendee(value);
                                }}
                                onOpen={() => {}}
                                onClose={() => {
                                  setEventAttendees([]);
                                }}
                                isOptionEqualToValue={(option: any, value: any) =>
                                  option.name === value.name
                                }
                                getOptionLabel={(option: any) => option.name}
                                options={eventAttendees}
                                loading={attendeesLoading}
                                filterOptions={(options: any, state: any) => {
                                  filter(options, state);

                                  // the search is implemented at the API level
                                  // we show all the options returned by the API instead of filtering them
                                  return options;
                                }}
                                renderInput={(params) => (
                                  <FdTextField
                                    {...params}
                                    register={register}
                                    label="Attendee"
                                    name="attendee"
                                    required
                                    InputProps={{
                                      ...params.InputProps,
                                      sx: classes.textFieldWithAdornment,
                                      onChange: handleAttendeeSearch,
                                    }}
                                    onChange={onChange}
                                    value={value}
                                    error={!!errors.attendee}
                                    errorMsg={errors.attendee && "this field is required"}
                                  />
                                )}
                                disabled={reservationDetails ? true : false}
                              />
                            </>
                          )}
                        />
                      </Box>

                      {/* amount input */}
                      <Box marginBottom="15px">
                        <FdTextField
                          register={register}
                          name="amount"
                          label="Amount"
                          type="number"
                        />
                      </Box>

                      {/* message input */}
                      <Box marginBottom="15px">
                        <FdTextField
                          register={register}
                          name="message"
                          label="Message"
                          multiline
                          rows={6}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* <FormSpy
                    subscription={{ pristine: true }}
                    onChange={(props) => {
                      setTimeout(() => {
                        if (!dirtyForm && !props.pristine)
                          setDirtyForm(!props.pristine);
                      }, 0);
                    }}
                  /> */}

                  {/* this is for showing a warning when leaving the form with unsaved updates */}
                  <RouteLeavingGuard
                    when={isDirty}
                    navigate={(path) => navigate(path)}
                    shouldBlockNavigation={(location) => isDirty}
                  />
                </form>
              {/* );
            }}
          /> */}
        </Box>
      </Box>
    </Box>

    <NotificationMessage ref={notificationRef} />

    {confirmationDialog.open && (
      <SimpleActionDialog
        type={confirmationDialog.type}
        icon={confirmationDialog.icon}
        open={confirmationDialog.open}
        title={confirmationDialog.title}
        isLoading={confirmationDialog.isLoading}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={confirmationDialog.onCancel}
        onClose={handleDialogClose}
        content={confirmationDialog.content}
        confirmLabel={confirmationDialog.confirmLabel}
      />
    )}
  </>;
};

export default ReservationEdit;

